import { Editor, Node as TiptapNode, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { Plugin } from "@tiptap/pm/state";
import { resolveLinkPresentation } from "../utils/html.js";
import { ChecklistWrapDecoration } from "./checklistWrapDecoration.js";

const URL_REGEX = /(\b(?:https?|ftp):\/\/[^\s<>"]+|#(?:project-\d+|task-\d+|tasks\?task=[^\s<>"]+|docs\?doc=[^\s<>"]+))/gi;
const URL_AT_END_REGEX = /(\b(?:https?|ftp):\/\/[^\s<>"]+|#(?:project-\d+|task-\d+|tasks\?task=[^\s<>"]+|docs\?doc=[^\s<>"]+))$/i;

const LinkChip = TiptapNode.create({
    name: "linkChip",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,

    addAttributes() {
        return {
            href: {
                default: ""
            },
            label: {
                default: ""
            },
            variant: {
                default: "external"
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: "a[data-link-chip]",
                getAttrs: (element) => buildLinkChipAttrs(
                    element.getAttribute("href") || "",
                    element.getAttribute("data-link-label") || element.textContent || ""
                )
            },
            {
                tag: "a[href]",
                getAttrs: (element) => buildLinkChipAttrs(
                    element.getAttribute("href") || "",
                    element.textContent || ""
                )
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const variant = HTMLAttributes.variant || "external";
        const label = HTMLAttributes.label || HTMLAttributes.href || "";
        const href = HTMLAttributes.href || "";
        const attrs = mergeAttributes(
            {
                href,
                "data-link-chip": "true",
                "data-link-label": label,
                "data-link-variant": variant,
                class: `docs-internal-link docs-internal-link--${variant} description-link-chip-node`,
                contenteditable: "false",
                spellcheck: "false"
            },
            HTMLAttributes
        );

        if (variant === "task") {
            delete attrs.target;
            delete attrs.rel;
        } else {
            attrs.target = "_blank";
            attrs.rel = "noopener noreferrer";
        }

        return ["a", attrs, label];
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                appendTransaction(transactions, _oldState, newState) {
                    if (!transactions.some(tr => tr.docChanged)) return null;
                    const ATOM_NODES = ["linkChip", "statusChip"];
                    let tr = null;
                    newState.doc.descendants((node, pos, parent) => {
                        if (!ATOM_NODES.includes(node.type.name)) return;
                        const afterPos = pos + node.nodeSize;
                        const $after = newState.doc.resolve(afterPos);
                        const nodeAfter = $after.nodeAfter;
                        // Only separate two adjacent atom chips. We deliberately do NOT
                        // force a trailing space when the chip is last in its block:
                        // that space regenerated on every edit and trapped the caret,
                        // so Backspace could never delete back past the chip.
                        if (nodeAfter && ATOM_NODES.includes(nodeAfter.type.name)) {
                            if (!tr) tr = newState.tr;
                            tr.insertText(" ", tr.mapping.map(afterPos));
                        }
                    });
                    return tr;
                }
            })
        ];
    }
});

function formatDateChipDisplay(isoDate) {
    if (!isoDate) return "Date";
    try {
        const [year, month, day] = String(isoDate).split("-").map(Number);
        return new Date(year, month - 1, day).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    } catch {
        return isoDate;
    }
}

function getActiveTaskDateChipSelection(state) {
    const { selection } = state || {};
    if (selection?.node?.type?.name === "dateChip") {
        return { node: selection.node, pos: selection.from };
    }

    const $from = selection?.$from;
    if ($from?.nodeBefore?.type?.name === "dateChip") {
        return { node: $from.nodeBefore, pos: $from.pos - $from.nodeBefore.nodeSize };
    }
    if ($from?.nodeAfter?.type?.name === "dateChip") {
        return { node: $from.nodeAfter, pos: $from.pos };
    }

    return null;
}

const DateChip = TiptapNode.create({
    name: "dateChip",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,

    addAttributes() {
        return {
            date: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-date") || "",
                renderHTML: (attributes) => ({ "data-date": attributes.date || "" })
            }
        };
    },

    parseHTML() {
        return [{ tag: "span[data-task-date-chip]" }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const display = formatDateChipDisplay(node.attrs.date);
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-task-date-chip": "",
                "data-date": node.attrs.date,
                class: "docs-date-chip task-date-chip"
            }),
            display
        ];
    },

    addCommands() {
        return {
            insertDateChip:
                ({ date = "" } = {}) =>
                ({ commands }) =>
                    commands.insertContent([
                        { type: this.name, attrs: { date } },
                        { type: "text", text: " " }
                    ]),
            updateDateChip:
                ({ date = "" } = {}) =>
                ({ state, dispatch }) => {
                    const chip = getActiveTaskDateChipSelection(state);
                    if (!chip) return false;
                    dispatch(state.tr.setNodeMarkup(chip.pos, undefined, { date }));
                    return true;
                }
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleTextInput(view, from, _to, text) {
                        if (text !== " ") return false;
                        const $from = view.state.doc.resolve(from);
                        const textBefore = $from.parent.textBetween(
                            Math.max(0, $from.parentOffset - 5),
                            $from.parentOffset,
                            null,
                            "\ufffc"
                        );
                        if (textBefore !== "/date") return false;

                        const coords = view.coordsAtPos(from);
                        const transaction = view.state.tr.delete(from - 5, from);
                        view.dispatch(transaction);

                        if (typeof document !== "undefined") {
                            document.dispatchEvent(new CustomEvent("task-date-chip-trigger", {
                                detail: { x: coords.left, y: coords.bottom }
                            }));
                        }
                        return true;
                    }
                }
            })
        ];
    }
});

let taskEditor = null;
let taskEditorElement = null;
let taskEditorHiddenField = null;
let taskEditorKeydownHandler = null;
let taskEditorHooks = {
    onFocus: null,
    onBlur: null,
    onUpdate: null
};
let projectEditor = null;
let projectEditorElement = null;
let projectEditorHiddenField = null;
let projectEditorKeydownHandler = null;
let projectEditorHooks = {
    onFocus: null,
    onBlur: null,
    onUpdate: null
};
let descriptionLinkPresentationContextResolver = () => ({});

export function setDescriptionLinkPresentationContextResolver(resolver) {
    descriptionLinkPresentationContextResolver =
        typeof resolver === "function" ? resolver : () => ({});
}

function getDescriptionLinkPresentationContext() {
    try {
        return descriptionLinkPresentationContextResolver?.() || {};
    } catch (error) {
        return {};
    }
}

function buildLinkChipAttrs(href, fallbackLabel = "") {
    const normalizedHref = String(href || "").trim();
    const presentation = resolveLinkPresentation(normalizedHref, getDescriptionLinkPresentationContext()) || null;

    return {
        href: presentation?.href || normalizedHref,
        label: presentation?.displayLabel || String(fallbackLabel || normalizedHref),
        variant: presentation?.variant || "external"
    };
}

function createLinkChipNodeJSON(href, fallbackLabel = "") {
    return {
        type: "linkChip",
        attrs: buildLinkChipAttrs(href, fallbackLabel)
    };
}

function getRawUrlReplacements(doc, schema) {
    const replacements = [];
    const linkChipType = schema.nodes.linkChip;
    if (!linkChipType) return replacements;

    doc.descendants((node, pos, parent) => {
        if (!node?.isText || !node.text) return true;
        if (parent?.type?.name === "codeBlock") return true;
        if (node.marks?.some((mark) => mark?.type?.name === "code")) return true;

        URL_REGEX.lastIndex = 0;
        let match = URL_REGEX.exec(node.text);
        while (match) {
            const url = match[0];
            const from = pos + match.index;
            const to = from + url.length;
            replacements.push({
                from,
                to,
                node: linkChipType.create(buildLinkChipAttrs(url))
            });
            match = URL_REGEX.exec(node.text);
        }

        return true;
    });

    return replacements.sort((a, b) => b.from - a.from);
}

function convertRawUrlsInEditor(editor) {
    if (!editor?.state?.schema?.nodes?.linkChip) return false;
    const replacements = getRawUrlReplacements(editor.state.doc, editor.state.schema);
    if (replacements.length === 0) return false;

    let tr = editor.state.tr;
    replacements.forEach(({ from, to, node }) => {
        tr = tr.replaceWith(from, to, node);
    });

    editor.view.dispatch(tr);
    return true;
}

function replaceTrailingUrlWithChip(editor, delimiter = " ") {
    if (!editor?.state?.schema?.nodes?.linkChip) return false;

    const { selection } = editor.state;
    if (!selection.empty) return false;

    const parent = selection.$from.parent;
    if (!parent?.isTextblock) return false;

    const textBefore = parent.textBetween(0, selection.$from.parentOffset, "\n", "\n");
    const match = textBefore.match(URL_AT_END_REGEX);
    if (!match) return false;

    const url = match[1];
    const from = selection.from - url.length;
    const to = selection.from;
    const chipNode = editor.state.schema.nodes.linkChip.create(buildLinkChipAttrs(url));

    let tr = editor.state.tr.replaceWith(from, to, chipNode);
    const insertPos = from + chipNode.nodeSize;

    if (delimiter) {
        tr = tr.insertText(delimiter, insertPos);
        tr = tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(insertPos + delimiter.length)));
    } else {
        tr = tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(insertPos)));
    }

    editor.view.dispatch(tr);
    return true;
}

function normalizeInputHtml(html) {
    return typeof html === "string" ? html : "";
}

export function normalizeOutputHtml(html) {
    const trimmed = normalizeInputHtml(html).trim();
    if (trimmed === "<p></p>" || trimmed === "<p><br></p>") {
        return "";
    }
    return trimmed;
}

function syncHiddenField(html) {
    if (!taskEditorHiddenField) return;
    taskEditorHiddenField.value = normalizeOutputHtml(html);
}

function syncProjectHiddenField(text) {
    if (!projectEditorHiddenField) return;
    projectEditorHiddenField.value = typeof text === "string" ? text : "";
}

function bindTaskEditorKeydownHandler() {
    if (!taskEditorElement) return;
    if (taskEditorKeydownHandler) {
        taskEditorElement.removeEventListener("keydown", taskEditorKeydownHandler);
    }

    taskEditorKeydownHandler = (event) => {
        if (!taskEditor) return;

        if (event.key === " " || event.key === "Tab") {
            const delimiter = event.key === "Tab" ? "\t" : " ";
            const replaced = replaceTrailingUrlWithChip(taskEditor, delimiter);
            if (replaced) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        }

        if (event.key === "Enter") {
            convertRawUrlsInEditor(taskEditor);
        }

        // List keys (Backspace / Delete / Enter inside a checkbox) are owned by
        // ProseMirror's native taskList keymap. We deliberately do NOT intercept
        // them here: doing so fired a second list transaction on top of
        // ProseMirror's, which produced spacing chaos and, in Chrome, left the
        // editor view out of sync so collapsed Backspace silently stopped working.
    };

    taskEditorElement.addEventListener("keydown", taskEditorKeydownHandler);
}

function unbindTaskEditorKeydownHandler() {
    if (!taskEditorElement || !taskEditorKeydownHandler) return;
    taskEditorElement.removeEventListener("keydown", taskEditorKeydownHandler);
    taskEditorKeydownHandler = null;
}

// Convert previous custom checklist markup to TipTap taskList format.
function convertLegacyChecklistHtml(html) {
    const raw = normalizeInputHtml(html);
    if (!raw || typeof document === "undefined") return raw;

    const hasLegacyCbItem = raw.includes("class=\"cb-item\"");
    const hasLegacyCheckboxRow = raw.includes("checkbox-row");
    if (!hasLegacyCbItem && !hasLegacyCheckboxRow) {
        return raw;
    }

    const container = document.createElement("div");
    container.innerHTML = raw;

    const legacyNodes = Array.from(container.querySelectorAll(".cb-item, .checkbox-row"));
    legacyNodes.forEach((node) => {
        const isLegacyRow = node.classList.contains("checkbox-row");
        const isChecked = isLegacyRow
            ? !!node.querySelector(".checkbox-toggle.checked, .checkbox-toggle[aria-pressed=\"true\"]")
            : node.getAttribute("data-checked") === "true";

        let textHtml = "";
        if (isLegacyRow) {
            const textNode = node.querySelector(".check-text");
            textHtml = textNode ? textNode.innerHTML : "";
        } else {
            textHtml = node.innerHTML;
        }

        const list = document.createElement("ul");
        list.setAttribute("data-type", "taskList");

        const item = document.createElement("li");
        item.setAttribute("data-type", "taskItem");
        item.setAttribute("data-checked", isChecked ? "true" : "false");

        const paragraph = document.createElement("p");
        const cleaned = (textHtml || "").trim();
        paragraph.innerHTML = cleaned.length > 0 ? cleaned : "<br>";

        item.appendChild(paragraph);
        list.appendChild(item);
        node.replaceWith(list);
    });

    // Merge adjacent task lists created from legacy checkbox rows.
    const lists = Array.from(container.querySelectorAll('ul[data-type="taskList"]'));
    lists.forEach((list) => {
        let next = list.nextElementSibling;
        while (next && next.matches('ul[data-type="taskList"]')) {
            while (next.firstElementChild) {
                list.appendChild(next.firstElementChild);
            }
            const toRemove = next;
            next = next.nextElementSibling;
            toRemove.remove();
        }
    });

    return container.innerHTML;
}

function getEditorElement() {
    if (taskEditorElement) return taskEditorElement;
    if (typeof document === "undefined") return null;
    taskEditorElement = document.querySelector("#task-description-editor");
    return taskEditorElement;
}

function getProjectEditorElement() {
    if (projectEditorElement) return projectEditorElement;
    if (typeof document === "undefined") return null;
    projectEditorElement = document.querySelector("#project-description-editor");
    return projectEditorElement;
}

function getProjectHiddenFieldElement() {
    if (projectEditorHiddenField) return projectEditorHiddenField;
    if (typeof document === "undefined") return null;
    projectEditorHiddenField = document.querySelector("#project-description-hidden");
    return projectEditorHiddenField;
}

function getHiddenFieldElement() {
    if (taskEditorHiddenField) return taskEditorHiddenField;
    if (typeof document === "undefined") return null;
    taskEditorHiddenField = document.querySelector("#task-description-hidden");
    return taskEditorHiddenField;
}

function getEditorRootClassName(editorElement) {
    const classTokens = (editorElement?.className || "")
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean)
        .filter((token) => token !== "tiptap" && !token.startsWith("ProseMirror"));

    if (!classTokens.includes("editor-content")) {
        classTokens.push("editor-content");
    }
    if (!classTokens.includes("focus:outline-none")) {
        classTokens.push("focus:outline-none");
    }

    return classTokens.join(" ");
}

function normalizePlainText(text = "") {
    return String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");
}

function createProjectParagraphContent(line) {
    if (!line) return null;

    const nodes = [];
    let lastIndex = 0;

    URL_REGEX.lastIndex = 0;
    let match = URL_REGEX.exec(line);
    while (match) {
        const url = match[0];
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            nodes.push({
                type: "text",
                text: line.slice(lastIndex, matchIndex)
            });
        }

        nodes.push(createLinkChipNodeJSON(url));

        lastIndex = matchIndex + url.length;
        match = URL_REGEX.exec(line);
    }

    if (lastIndex < line.length) {
        nodes.push({
            type: "text",
            text: line.slice(lastIndex)
        });
    }

    return nodes.length > 0 ? nodes : null;
}

export function projectPlainTextToDoc(text = "") {
    const normalizedText = normalizePlainText(text);
    const lines = normalizedText.split("\n");

    return {
        type: "doc",
        content: (lines.length > 0 ? lines : [""]).map((line) => {
            const paragraph = { type: "paragraph" };
            const content = createProjectParagraphContent(line);
            if (content) {
                paragraph.content = content;
            }
            return paragraph;
        })
    };
}

function serializeProjectInlineContent(content = []) {
    let text = "";
    let pendingLinkHref = null;

    const flushPendingLink = () => {
        if (!pendingLinkHref) return;
        text += pendingLinkHref;
        pendingLinkHref = null;
    };

    content.forEach((node) => {
        if (!node) return;

        if (node.type === "hardBreak") {
            flushPendingLink();
            text += "\n";
            return;
        }

        if (node.type === "linkChip") {
            flushPendingLink();
            text += node.attrs?.href || "";
            return;
        }

        if (node.type === "text") {
            const linkMark = Array.isArray(node.marks)
                ? node.marks.find((mark) => mark?.type === "link" && mark.attrs?.href)
                : null;

            if (linkMark?.attrs?.href) {
                if (pendingLinkHref !== linkMark.attrs.href) {
                    flushPendingLink();
                    pendingLinkHref = linkMark.attrs.href;
                }
                return;
            }

            flushPendingLink();
            text += node.text || "";
            return;
        }

        if (Array.isArray(node.content)) {
            flushPendingLink();
            text += serializeProjectInlineContent(node.content);
        }
    });

    flushPendingLink();
    return text;
}

export function projectDocToPlainText(doc) {
    if (!doc || !Array.isArray(doc.content)) return "";

    return doc.content
        .map((node) => {
            if (!node || node.type !== "paragraph") return "";
            return serializeProjectInlineContent(node.content || []);
        })
        .join("\n")
        .replace(/\u200B/g, "")
        .replace(/\u00A0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n+$/g, "");
}

export function initializeTaskDescriptionEditor({
    editorSelector = "#task-description-editor",
    hiddenSelector = "#task-description-hidden",
    placeholder = "",
    onFocus = null,
    onBlur = null,
    onUpdate = null
} = {}) {
    if (typeof document === "undefined") return null;

    const editorElement = typeof editorSelector === "string"
        ? document.querySelector(editorSelector)
        : editorSelector;
    const hiddenField = typeof hiddenSelector === "string"
        ? document.querySelector(hiddenSelector)
        : hiddenSelector;

    if (!editorElement) return null;

    taskEditorHooks = {
        onFocus: typeof onFocus === "function" ? onFocus : null,
        onBlur: typeof onBlur === "function" ? onBlur : null,
        onUpdate: typeof onUpdate === "function" ? onUpdate : null
    };

    const editorRootId = editorElement.id || "task-description-editor";
    const editorRootClass = getEditorRootClassName(editorElement);
    const isSameElement = !!taskEditor && taskEditorElement === editorElement;

    if (isSameElement) {
        taskEditorElement = editorElement;
        taskEditorHiddenField = hiddenField || null;
        syncHiddenField(taskEditor.getHTML());
        return taskEditor;
    }

    if (taskEditor) {
        unbindTaskEditorKeydownHandler();
        taskEditor.destroy();
        taskEditor = null;
    }

    taskEditorElement = editorElement;
    taskEditorHiddenField = hiddenField || null;

    taskEditor = new Editor({
        element: editorElement,
        editorProps: {
            attributes: {
                id: editorRootId,
                class: editorRootClass
            }
        },
        extensions: [
            StarterKit.configure({
                link: false,
                underline: false,
            }),
            Underline,
            TaskList,
            TaskItem.configure({ nested: false }),
            ChecklistWrapDecoration,
            LinkChip,
            DateChip,
            Placeholder.configure({
                placeholder
            })
        ],
        content: convertLegacyChecklistHtml(editorElement.innerHTML || ""),
        onCreate: ({ editor }) => {
            convertRawUrlsInEditor(editor);
            const html = normalizeOutputHtml(editor.getHTML());
            syncHiddenField(html);
            if (taskEditorHooks.onUpdate) taskEditorHooks.onUpdate(html);
        },
        onUpdate: ({ editor }) => {
            const html = normalizeOutputHtml(editor.getHTML());
            syncHiddenField(html);
            if (taskEditorHooks.onUpdate) taskEditorHooks.onUpdate(html);
        },
        onFocus: ({ editor }) => {
            if (!taskEditorHooks.onFocus) return;
            taskEditorHooks.onFocus(normalizeOutputHtml(editor.getHTML()));
        },
        onBlur: ({ editor }) => {
            convertRawUrlsInEditor(editor);
            const html = normalizeOutputHtml(editor.getHTML());
            syncHiddenField(html);
            if (taskEditorHooks.onBlur) taskEditorHooks.onBlur(html);
        }
    });

    bindTaskEditorKeydownHandler();

    return taskEditor;
}

export function focusTaskDescriptionEditor() {
    if (!taskEditor) return false;
    return taskEditor.commands.focus();
}

export function getTaskDescriptionDateChipState() {
    if (!taskEditor) return { active: false, date: "" };
    const chip = getActiveTaskDateChipSelection(taskEditor.state);
    if (!chip) return { active: false, date: "" };
    return { active: true, date: chip.node.attrs.date || "" };
}

export function getTaskDescriptionHTML() {
    if (taskEditor) {
        return normalizeOutputHtml(taskEditor.getHTML());
    }

    const editorElement = getEditorElement();
    if (!editorElement) return "";
    return normalizeOutputHtml(editorElement.innerHTML);
}

function bindProjectEditorKeydownHandler() {
    if (!projectEditorElement) return;
    if (projectEditorKeydownHandler) {
        projectEditorElement.removeEventListener("keydown", projectEditorKeydownHandler);
    }

    projectEditorKeydownHandler = (event) => {
        if (!projectEditor) return;

        if (event.key === " " || event.key === "Tab") {
            const delimiter = event.key === "Tab" ? "\t" : " ";
            const replaced = replaceTrailingUrlWithChip(projectEditor, delimiter);
            if (replaced) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        }

        if (event.key === "Enter") {
            convertRawUrlsInEditor(projectEditor);
        }
    };

    projectEditorElement.addEventListener("keydown", projectEditorKeydownHandler);
}

function unbindProjectEditorKeydownHandler() {
    if (!projectEditorElement || !projectEditorKeydownHandler) return;
    projectEditorElement.removeEventListener("keydown", projectEditorKeydownHandler);
    projectEditorKeydownHandler = null;
}

export function initializeProjectDescriptionEditor({
    editorSelector = "#project-description-editor",
    hiddenSelector = "#project-description-hidden",
    content = "",
    placeholder = "",
    onFocus = null,
    onBlur = null,
    onUpdate = null
} = {}) {
    if (typeof document === "undefined") return null;

    const editorElement = typeof editorSelector === "string"
        ? document.querySelector(editorSelector)
        : editorSelector;
    const hiddenField = typeof hiddenSelector === "string"
        ? document.querySelector(hiddenSelector)
        : hiddenSelector;

    if (!editorElement) return null;

    projectEditorHooks = {
        onFocus: typeof onFocus === "function" ? onFocus : null,
        onBlur: typeof onBlur === "function" ? onBlur : null,
        onUpdate: typeof onUpdate === "function" ? onUpdate : null
    };

    const editorRootId = editorElement.id || "project-description-editor";
    const editorRootClass = getEditorRootClassName(editorElement);
    const isSameElement = !!projectEditor && projectEditorElement === editorElement;

    if (isSameElement) {
        projectEditorElement = editorElement;
        projectEditorHiddenField = hiddenField || null;
        const currentHtml = normalizeOutputHtml(projectEditor.getHTML());
        syncProjectHiddenField(currentHtml);
        return projectEditor;
    }

    if (projectEditor) {
        unbindProjectEditorKeydownHandler();
        projectEditor.destroy();
        projectEditor = null;
    }

    projectEditorElement = editorElement;
    projectEditorHiddenField = hiddenField || null;

    projectEditor = new Editor({
        element: editorElement,
        editorProps: {
            attributes: {
                id: editorRootId,
                class: editorRootClass
            }
        },
        extensions: [
            StarterKit.configure({
                link: false,
                underline: false,
            }),
            Underline,
            TaskList,
            TaskItem.configure({ nested: false }),
            ChecklistWrapDecoration,
            LinkChip,
            DateChip,
            Placeholder.configure({
                placeholder
            })
        ],
        content: convertLegacyChecklistHtml(content || ""),
        onCreate: ({ editor }) => {
            convertRawUrlsInEditor(editor);
            const html = normalizeOutputHtml(editor.getHTML());
            syncProjectHiddenField(html);
        },
        onUpdate: ({ editor }) => {
            const html = normalizeOutputHtml(editor.getHTML());
            syncProjectHiddenField(html);
            if (projectEditorHooks.onUpdate) projectEditorHooks.onUpdate(html);
        },
        onFocus: ({ editor }) => {
            if (!projectEditorHooks.onFocus) return;
            projectEditorHooks.onFocus(normalizeOutputHtml(editor.getHTML()));
        },
        onBlur: ({ editor }) => {
            convertRawUrlsInEditor(editor);
            const html = normalizeOutputHtml(editor.getHTML());
            syncProjectHiddenField(html);
            if (projectEditorHooks.onBlur) projectEditorHooks.onBlur(html);
        }
    });

    bindProjectEditorKeydownHandler();

    return projectEditor;
}

export function getProjectDescriptionHTML() {
    if (projectEditor) {
        return normalizeOutputHtml(projectEditor.getHTML());
    }

    const hiddenField = getProjectHiddenFieldElement();
    if (hiddenField) return hiddenField.value || "";

    const editorElement = getProjectEditorElement();
    if (!editorElement) return "";
    return normalizeOutputHtml(editorElement.innerHTML || "");
}

export function getProjectDescriptionText() {
    const html = getProjectDescriptionHTML();
    if (!html) return "";
    if (typeof document !== "undefined") {
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
    }
    return html.replace(/<[^>]+>/g, "");
}

export function setProjectDescriptionHTML(html = "") {
    const normalized = convertLegacyChecklistHtml(normalizeInputHtml(html));

    if (projectEditor) {
        projectEditor.commands.setContent(normalized, { emitUpdate: false });
        const savedHtml = normalizeOutputHtml(projectEditor.getHTML());
        syncProjectHiddenField(savedHtml);
        return;
    }

    const editorElement = getProjectEditorElement();
    if (editorElement) {
        editorElement.innerHTML = normalized;
    }

    syncProjectHiddenField(normalized);
}

export function setProjectDescriptionText(text = "") {
    setProjectDescriptionHTML(typeof text === "string" ? text : String(text || ""));
}

export function clearProjectDescriptionEditor() {
    setProjectDescriptionHTML("");
}

export function runProjectDescriptionCommand(command) {
    if (!projectEditor) return false;

    switch (command) {
        case "bold":
            return projectEditor.chain().focus().toggleBold().run();
        case "italic":
            return projectEditor.chain().focus().toggleItalic().run();
        case "underline":
            return projectEditor.chain().focus().toggleUnderline().run();
        case "strikeThrough":
            return projectEditor.chain().focus().toggleStrike().run();
        case "insertUnorderedList":
            return projectEditor.chain().focus().toggleBulletList().run();
        case "insertOrderedList":
            return projectEditor.chain().focus().toggleOrderedList().run();
        default:
            return false;
    }
}

export function insertProjectDescriptionHeading(level = "h2") {
    if (!projectEditor) return false;
    const levelMap = { h1: 1, h2: 2, h3: 3 };
    const numLevel = levelMap[level] || 2;
    return projectEditor.chain().focus().toggleHeading({ level: numLevel }).run();
}

export function insertProjectDescriptionDivider() {
    if (!projectEditor) return false;
    return projectEditor.chain().focus().setHorizontalRule().run();
}

export function insertProjectDescriptionChecklist() {
    if (!projectEditor) return false;
    return projectEditor.chain().focus().toggleTaskList().run();
}

export function insertProjectDescriptionDateChip({ date = "" } = {}) {
    if (!projectEditor) return false;
    return projectEditor.chain().focus().insertDateChip({ date }).run();
}

export function updateProjectDescriptionDateChip({ date = "" } = {}) {
    if (!projectEditor) return false;
    return projectEditor.chain().focus().updateDateChip({ date }).run();
}

export function getProjectDescriptionDateChipState() {
    if (!projectEditor) return { active: false, date: "" };
    const chip = getActiveTaskDateChipSelection(projectEditor.state);
    if (!chip) return { active: false, date: "" };
    return { active: true, date: chip.node.attrs.date || "" };
}

export function focusProjectDescriptionEditor(position = null) {
    if (!projectEditor) return false;
    return projectEditor.commands.focus(position);
}

export function destroyProjectDescriptionEditor() {
    if (!projectEditor) return;
    unbindProjectEditorKeydownHandler();
    projectEditor.destroy();
    projectEditor = null;
    projectEditorElement = null;
    projectEditorHiddenField = null;
}

export function setTaskDescriptionHTML(html = "") {
    const normalized = convertLegacyChecklistHtml(normalizeInputHtml(html));

    if (taskEditor) {
        taskEditor.commands.setContent(normalized, { emitUpdate: false });
        syncHiddenField(taskEditor.getHTML());
        return;
    }

    const editorElement = getEditorElement();
    if (editorElement) {
        editorElement.innerHTML = normalized;
    }

    const hiddenField = getHiddenFieldElement();
    if (hiddenField) {
        hiddenField.value = normalizeOutputHtml(normalized);
    }
}

export function clearTaskDescriptionEditor() {
    setTaskDescriptionHTML("");
}

export function runTaskDescriptionCommand(command) {
    if (!taskEditor) return false;

    switch (command) {
        case "bold":
            return taskEditor.chain().focus().toggleBold().run();
        case "italic":
            return taskEditor.chain().focus().toggleItalic().run();
        case "underline":
            return taskEditor.chain().focus().toggleUnderline().run();
        case "strikeThrough":
            return taskEditor.chain().focus().toggleStrike().run();
        case "insertUnorderedList":
            return taskEditor.chain().focus().toggleBulletList().run();
        case "insertOrderedList":
            return taskEditor.chain().focus().toggleOrderedList().run();
        default:
            return false;
    }
}

export function insertTaskDescriptionHeading(level) {
    if (!taskEditor) return false;
    const headingLevel = Number.parseInt(String(level).replace("h", ""), 10);
    if (!Number.isFinite(headingLevel) || headingLevel < 1 || headingLevel > 3) {
        return false;
    }
    return taskEditor.chain().focus().toggleHeading({ level: headingLevel }).run();
}

export function insertTaskDescriptionDivider() {
    if (!taskEditor) return false;
    return taskEditor.chain().focus().setHorizontalRule().run();
}

export function insertTaskDescriptionChecklist() {
    if (!taskEditor) return false;
    if (taskEditor.isActive("taskItem") || taskEditor.isActive("taskList")) {
        const lifted = taskEditor.chain().focus().liftListItem("taskItem").run();
        if (lifted) return true;
    }
    return taskEditor.chain().focus().toggleTaskList().run();
}

export function insertTaskDescriptionDateChip(date = "") {
    if (!taskEditor) return false;
    return taskEditor.chain().focus().insertDateChip({ date }).run();
}

export function updateTaskDescriptionDateChip(date = "") {
    if (!taskEditor) return false;
    return taskEditor.chain().focus().updateDateChip({ date }).run();
}
